import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { Virtuoso } from 'react-virtuoso';
import { getLocale } from 'js/store/app/App.selectors';

// utils
import { isDeviceIOS } from 'js/utils/iosDetection';

// import hooks
import { useRouteInfo } from 'js/hooks/useRouteInfo';

// import actions
import { toggleActivityCardFocus } from 'js/store/app/App.actions';

// import components
import ActivityCard from 'js/components/activity-card/ActivityCard';
import LoadingIcon from 'js/components/generic/Generic.loading-icon';
import { trackEvent } from '../../utils/tracking';

let timer = null;

const GridCardList = ({ cards, fromSearch = false }) => {
  const dispatch = useDispatch();
  const locale = useSelector((state) => getLocale(state));
  const [activeCard, setActiveCard] = useState(null);
  const cardRefs = useRef({});

  const scrollToCard = (id, overflow = false) => {
    const index = cards.findIndex((card) => card.id === id);
    if (index === -1) return;

    if (virtuoso.current) {
      setTimeout(() => {
        virtuoso.current.scrollToIndex({
          index,
          align: 'start',
          behavior: 'smooth',
          offset: -85,
        });
      }, 500);

      if (overflow) {
        toggleOverflow(true);
      }
    }
  };
  const handleFlip = (id) => {
    if (fromSearch && id !== activeCard) {
      const selectedPlayType = fromSearch.playType[0] ?? '';
      const selectedPlayers = fromSearch.players[0] ?? '';
      const selectedMinutes = fromSearch.minutes[0] ?? '';
      trackEvent('game_content_found', {
        game_language: locale,
        category_name: fromSearch.activity,
        location_name: fromSearch.playType.join(','),
        player_count: fromSearch.players.join(','),
        time_available: fromSearch.minutes.join(','),
        content_id: id,
      });
    }
    dispatch(toggleActivityCardFocus(id === activeCard ? false : true));
    setActiveCard(id === activeCard ? null : id);

    if (id !== activeCard) {
      scrollToCard(id, true);
    } else {
      toggleOverflow(false);
      clearTimeout(timer);
    }
  };

  const toggleOverflow = (value) => {
    if (value) {
      document.body.style.overflow = 'hidden';
      isDeviceIOS() && (document.body.style.touchAction = 'none');
    } else {
      isDeviceIOS() && (document.body.style.touchAction = 'auto');
      document.body.style.overflow = '';
    }
  };

  // handle auto scroll to cardID from route state
  const { cardID = false } = useRouteInfo().state || {};
  useEffect(() => {
    // if (!cardID || cards.length === 0) return;
    // const index = cards.findIndex((card) => card.id === cardID);
    // if (index === 0) return;

    if (cardID) scrollToCard(cardID);

    return () => {
      setActiveCard(false);
      dispatch(toggleActivityCardFocus(false));
      isDeviceIOS() && (document.body.style.touchAction = 'auto');
      document.body.style.overflow = '';
    };
  }, []);

  const virtuoso = useRef(null);

  if (!cards || cards.length === 0) {
    return <LoadingIcon />;
  }

  return (
    <ul className="card-grid">
      <AnimatePresence>
        <Virtuoso
          ref={virtuoso}
          useWindowScroll
          minOverscanItemCount={{ top: 2, bottom: 2 }}
          style={{ height: '100%', width: '100%' }}
          data={cards}
          components={{ Footer: () => <div style={{ height: '150px' }} /> }}
          itemContent={(index, card) => {
            if (!card) return null;
            const { id } = card || {};
            const isActive = activeCard === id;
            const isHidden = activeCard !== null && !isActive;
            return (
              <motion.li
                key={id}
                ref={(el) => (cardRefs.current[id] = el)}
                layout
                animate={isHidden ? { opacity: 0 } : { opacity: 1 }}
                style={{
                  pointerEvents: isHidden ? 'none' : 'auto',
                  paddingBottom: '40px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <ActivityCard onFlip={() => handleFlip(id)} data={card} />
              </motion.li>
            );
          }}
        />
      </AnimatePresence>
    </ul>
  );
};

export default GridCardList;
